/**
 * Scoring Impact Builder
 * 
 * Deterministic synthesis of qualitative risk explanation for "Why This Costs You Strokes" section.
 * 
 * Rules:
 * - Never reuse raw compensation_chains[].evidence verbatim
 * - Never include numbers or promises
 * - Synthesize qualitative risk explanation
 * - Use fixed risk vocabulary
 */

/**
 * Risk vocabulary terms for scoring impact synthesis
 */
const RISK_VOCABULARY = {
  timing: ['timing-sensitive', 'timing-dependent', 'requires precise timing'],
  margin: ['low margin for error', 'narrow margin', 'little room for error'],
  contact: ['contact variability', 'inconsistent contact', 'contact inconsistency'],
  faceControl: ['face-control dependent', 'clubface-dependent', 'face-angle sensitive'],
  impact: ['inconsistent impact', 'impact variability', 'impact inconsistency'],
  distance: ['distance variability', 'inconsistent distance', 'distance inconsistency'],
  pressure: ['under pressure', 'when it matters most', 'in critical moments'],
} as const;

/**
 * Extract key phrases from compensation chain evidence
 */
function extractCompensationPatterns(compensationChains: any[]): string[] {
  const patterns: string[] = [];
  
  for (const chain of compensationChains) {
    const evidence = chain.evidence || '';
    if (typeof evidence === 'string' && evidence.trim()) {
      // Extract key action words (verbs) and compensation indicators
      const lowerEvidence = evidence.toLowerCase();
      
      if (lowerEvidence.includes('compensat') || lowerEvidence.includes('adjust')) {
        patterns.push('compensatory adjustment');
      }
      if (lowerEvidence.includes('overcorrect') || lowerEvidence.includes('over-compensat')) {
        patterns.push('overcorrection');
      }
      if (lowerEvidence.includes('timing') || lowerEvidence.includes('sequence')) {
        patterns.push('timing-dependent pattern');
      }
    }
  }
  
  return [...new Set(patterns)]; // Deduplicate
}

/**
 * Extract phase context from phase summaries
 */
function extractPhaseContext(phaseSummaries: any[]): string {
  const phases = phaseSummaries
    .map(phase => phase.phase)
    .filter(Boolean)
    .map((phase: string) => {
      // Map phase names to user-friendly terms
      const phaseMap: Record<string, string> = {
        'setup': 'setup',
        'takeaway': 'takeaway',
        'top': 'top of backswing',
        'transition': 'transition',
        'impact': 'impact',
        'follow_through': 'follow-through',
      };
      return phaseMap[phase.toLowerCase()] || phase;
    });

  if (phases.length === 0) {
    return 'swing';
  }

  // Return most relevant phase (usually impact or transition)
  if (phases.includes('impact')) return 'impact';
  if (phases.includes('transition')) return 'transition';
  return phases[0];
}

/**
 * Build synthesized scoring impact text
 * 
 * PLA-96: Updated to accept primaryFaultId explicitly to ensure
 * scoring risk is always based on the actionable pattern, not root causes.
 * 
 * Creates a qualitative risk explanation that:
 * - Describes how the primary fault + compensations create risk
 * - Uses fixed risk vocabulary
 * - Never includes numbers or promises
 * - Returns null if insufficient data
 * 
 * @param primaryFaultId - The primary fault ID (e.g., "early_extension")
 * @param rootCauses - Array of root cause objects (may or may not match primaryFaultId)
 * @param compensationChains - Array of compensation chain objects
 * @param phaseSummaries - Array of phase summary objects
 * @returns Synthesized paragraph (2-4 sentences) or null
 */
export function buildScoringImpactText(
  primaryFaultId: string,
  rootCauses: any[],
  compensationChains: any[],
  phaseSummaries: any[]
): string | null {
  // PLA-96: Validate primaryFaultId is provided
  if (!primaryFaultId || typeof primaryFaultId !== 'string') {
    return null;
  }

  // Get first root cause justification for context (if any)
  const firstRootCause = rootCauses[0];
  const hasJustification = firstRootCause?.justification && 
    typeof firstRootCause.justification === 'string' && 
    firstRootCause.justification.trim().length >= 20;

  // PLA-96: Filter compensation chains for the PRIMARY FAULT, not root cause
  const relevantChains = compensationChains.filter((cc: any) => {
    return cc.primary_flaw_id?.toLowerCase() === primaryFaultId.toLowerCase();
  });

  // Extract key information
  const compensationPatterns = extractCompensationPatterns(relevantChains);
  const phaseContext = extractPhaseContext(phaseSummaries);

  // Build synthesis
  const sentences: string[] = [];

  // Sentence 1: Primary fault creates compensation pattern
  if (compensationPatterns.length > 0) {
    const pattern = compensationPatterns[0];
    sentences.push(
      `This fault creates ${pattern}, which amplifies inconsistency at ${phaseContext}.`
    );
  } else {
    sentences.push(
      `This fault creates timing-sensitive patterns that amplify inconsistency at ${phaseContext}.`
    );
  }

  // Sentence 2: Risk amplification
  // PLA-96: Use PRIMARY FAULT ID for hash, not root cause flaw_id
  const hash = primaryFaultId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const riskTerms = [
    RISK_VOCABULARY.timing[0],
    RISK_VOCABULARY.margin[0],
    RISK_VOCABULARY.contact[0],
  ];
  const selectedRisk = riskTerms[hash % riskTerms.length];
  
  sentences.push(
    `The pattern is ${selectedRisk}, meaning contact and distance become variable.`
  );

  // Sentence 3: Outcome
  sentences.push(
    `The result is inconsistent performance ${RISK_VOCABULARY.pressure[0]}.`
  );

  // Combine into paragraph
  const paragraph = sentences.join(' ');

  // Validate length (100-200 chars is ideal, but allow 80-300 for flexibility)
  if (paragraph.length < 80 || paragraph.length > 300) {
    // Fallback: simpler version
    return `This fault creates timing-sensitive patterns that lead to inconsistent contact and distance, especially ${RISK_VOCABULARY.pressure[0]}.`;
  }

  return paragraph;
}

